import {
  formatOrderHistoryFieldLabel,
  formatOrderHistoryValue,
  sortOrderHistoriesDesc,
  type OrderHistory,
  type OrderHistoryChange,
  type OrderRevisionNote,
} from "../types/order";
import {
  sortUangJalanReportHistoriesDesc,
  type UangJalanReportHistory,
} from "../types/uangJalanReport";

export interface DisplayHistoryChange {
  field: string;
  before?: unknown;
  after?: unknown;
}

export interface DisplayHistoryEntry {
  userId: string;
  userName?: string;
  profilePictureUrl?: string | null;
  timestamp: number;
  changes: DisplayHistoryChange[];
}

type TranslateFn = (key: string) => string;

const isRevisionNoteCapturedInHistories = (histories: OrderHistory[], note: string): boolean => {
  return histories.some((history) =>
    history.changes.some((change) => change.field === "revisionNote" && change.after === note),
  );
};

export const mergeOrderRevisionNotesIntoHistories = (
  histories: OrderHistory[],
  revisionNotes: OrderRevisionNote[],
): DisplayHistoryEntry[] => {
  const syntheticEntries: DisplayHistoryEntry[] = revisionNotes.map((entry) => ({
    userId: entry.userId,
    timestamp: entry.timestamp,
    changes: [{ field: "revisionNote", after: entry.note }],
  }));

  const combined = [...histories, ...syntheticEntries];
  return mergeHistoryEntriesWithSameTimestamp(combined);
};

export const mergeHistoryEntriesWithSameTimestamp = (
  entries: DisplayHistoryEntry[],
): DisplayHistoryEntry[] => {
  const sorted = [...entries].sort((a, b) => b.timestamp - a.timestamp);
  const merged: DisplayHistoryEntry[] = [];

  for (const entry of sorted) {
    const existing = merged.find(
      (candidate) => candidate.timestamp === entry.timestamp && candidate.userId === entry.userId,
    );

    if (existing) {
      existing.changes = [...existing.changes, ...entry.changes];
      if (!existing.userName && entry.userName) {
        existing.userName = entry.userName;
      }
      if (!existing.profilePictureUrl && entry.profilePictureUrl) {
        existing.profilePictureUrl = entry.profilePictureUrl;
      }
      continue;
    }

    merged.push({
      ...entry,
      changes: [...entry.changes],
    });
  }

  return merged;
};

export const buildOrderDisplayHistories = (
  histories: OrderHistory[],
  revisionNote?: OrderRevisionNote | null,
): DisplayHistoryEntry[] => {
  const sorted = sortOrderHistoriesDesc(histories);

  if (!revisionNote?.note) {
    return sorted;
  }

  if (isRevisionNoteCapturedInHistories(sorted, revisionNote.note)) {
    return sorted;
  }

  return mergeOrderRevisionNotesIntoHistories(sorted, [revisionNote]);
};

export const buildUangJalanReportDisplayHistories = (
  histories: UangJalanReportHistory[],
): DisplayHistoryEntry[] => {
  return sortUangJalanReportHistoriesDesc(histories);
};

export const getHistoryStatusChange = (
  changes: DisplayHistoryChange[],
): { before?: string; after?: string } | null => {
  const statusChange = changes.find((change) => change.field === "status");
  if (!statusChange) {
    return null;
  }

  const before =
    typeof statusChange.before === "string" && statusChange.before
      ? statusChange.before
      : undefined;
  const after =
    typeof statusChange.after === "string" && statusChange.after ? statusChange.after : undefined;

  if (!before && !after) {
    return null;
  }

  return { before, after };
};

export const formatDisplayHistoryFieldLabel = (field: string, t: TranslateFn): string => {
  if (field === "revisionNote") {
    return t("orders.revisionNote");
  }
  if (field === "status") {
    return t("Status");
  }
  if (field === "orderIds") {
    return t("reports.orderIds");
  }
  if (field === "standbyOrderIds") {
    return t("reports.standbyOrderIds");
  }
  if (field === "date") {
    return t("standby.date");
  }
  if (field === "locationId") {
    return t("Location");
  }
  if (field === "expenses.standby") {
    return t("standby.expense");
  }
  return formatOrderHistoryFieldLabel(field, t);
};

export const formatDisplayHistoryValue = (
  field: string,
  value: unknown,
  t: TranslateFn,
): string => {
  if (field === "revisionNote" && typeof value === "string") {
    return value;
  }
  if (field === "status") {
    if (typeof value !== "string" || !value) {
      return "—";
    }
    return t(value);
  }
  if (field === "orderIds" || field === "standbyOrderIds") {
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    return formatOrderHistoryValue(field, value, t);
  }
  return formatOrderHistoryValue(field, value, t);
};

export const getOrderHistoryChange = (
  changes: OrderHistoryChange[],
  field: string,
): OrderHistoryChange | undefined => changes.find((change) => change.field === field);
